/**
 * GILGAL CUSTOM MEGA MENU START
 * Gilgal Medical Supplies — Enterprise Mega Menu Engine
 *
 * Key design decisions:
 *  1. Panels are portaled to <body> so position:fixed works even when
 *     the sticky header uses CSS transform (which would otherwise
 *     create a new stacking context and break fixed positioning).
 *  2. Show/hide is controlled via .is-open on the panel directly,
 *     NOT via CSS :hover descendant selectors (avoids theme CSS fights).
 *  3. Category hover (left col → subcategory panels) uses data-cat-idx.
 */

(function () {

  'use strict';

  const CLOSE_DELAY = 200; // ms grace period when mouse moves trigger → panel

  /* ── Utility ──────────────────────────────────────────────── */

  function $$(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  /** Returns the viewport-relative bottom edge of the nav bar */
  function getNavBarBottom() {
    const sticky = document.querySelector('.sticky-header.show');
    const el = sticky
      ? sticky.querySelector('.header-container--bottom')
      : document.querySelector('[data-sticky-header] .header-container--bottom') ||
        document.querySelector('.header-container--bottom');
    if (!el) return 72;
    return Math.round(el.getBoundingClientRect().bottom);
  }

  /**
   * Move a panel element to <body> so it is no longer inside any
   * overflow or transform context from the header.
   */
  function portalToBody(panel) {
    if (panel && panel.parentElement !== document.body) {
      panel.dataset.emmPortaled = '1';
      document.body.appendChild(panel);
    }
  }

  /* ── Panel controller ─────────────────────────────────────── */

  class EmmPanel {

    constructor(triggerEl) {
      this.trigger = triggerEl;
      // Find the panel BEFORE portaling (it's still a descendant here)
      this.panel = triggerEl.querySelector('.emm-panel, .emm-dropdown');
      this._closeT = null;
      this._open   = false;

      if (!this.panel) return;

      // Portal immediately so position:fixed works correctly
      portalToBody(this.panel);

      this._bind();
    }

    _bind() {
      // Hover: open immediately, close after grace period
      this.trigger.addEventListener('mouseenter', () => {
        this._cancelClose();
        this._openNow();
      });
      this.trigger.addEventListener('mouseleave', () => this._scheduleClose());

      // Keep panel open while cursor is inside it (panel is now in body)
      this.panel.addEventListener('mouseenter', () => this._cancelClose());
      this.panel.addEventListener('mouseleave', () => this._scheduleClose());

      // Click / Touch handling for the trigger link
      const triggerLink = this.trigger.querySelector(':scope > .menu-link');
      if (triggerLink) {
        // Desktop: prevent the link from navigating — panel opens via mouseenter
        triggerLink.addEventListener('click', (e) => {
          e.preventDefault();
        });

        // Touch: first tap opens panel, second tap follows href
        triggerLink.addEventListener('touchstart', (e) => {
          if (!this._open) {
            e.preventDefault();
            this._openNow();
          }
        }, { passive: false });
      }

      // Keyboard navigation
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._open ? this._closeNow() : this._openNow();
        }
        if (e.key === 'Escape') this._closeNow();
      });

      this.panel.addEventListener('keydown', (e) => {
        const links = $$('a', this.panel).filter(a => a.offsetParent !== null);
        const idx   = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          links[Math.min(idx + 1, links.length - 1)]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          links[Math.max(idx - 1, 0)]?.focus();
        } else if (e.key === 'Escape') {
          this._closeNow();
          triggerLink?.focus();
        } else if (e.key === 'Tab') {
          setTimeout(() => {
            if (!this.panel.contains(document.activeElement)) this._closeNow();
          }, 0);
        }
      });
    }

    _scheduleClose() {
      this._closeT = setTimeout(() => this._closeNow(), CLOSE_DELAY);
    }

    _cancelClose() { clearTimeout(this._closeT); }

    _openNow() {
      this._repositionPanel(); // position BEFORE making visible to avoid flash
      this._open = true;
      this.trigger.classList.add('focus');
      this.trigger.querySelector(':scope > .menu-link')
        ?.setAttribute('aria-expanded', 'true');
      this.panel.classList.add('is-open');
    }

    _closeNow() {
      this._cancelClose();
      this._open = false;
      this.trigger.classList.remove('focus');
      this.trigger.querySelector(':scope > .menu-link')
        ?.setAttribute('aria-expanded', 'false');
      this.panel.classList.remove('is-open');
    }

    _repositionPanel() {
      const navBottom = getNavBarBottom();
      this.panel.style.top = navBottom + 'px';

      if (this.panel.classList.contains('emm-dropdown')) {
        // Align dropdown under its trigger
        const r = this.trigger.getBoundingClientRect();
        this.panel.style.left     = Math.max(0, r.left) + 'px';
        this.panel.style.width    = 'auto';
        this.panel.style.minWidth = Math.max(240, r.width) + 'px';
      } else {
        // Full-width panel
        this.panel.style.left  = '0px';
        this.panel.style.width = '100vw';
      }
    }

  }

  /* ── Category subcategory hover ────────────────────────────
   * Left column: .gmm-cat-item[data-cat-idx]  (hover target)
   * Middle column: .gmm-subcat-panel[data-cat-idx] (shown panel)
   * ─────────────────────────────────────────────────────────── */

  function initCategoryHover() {
    $$('.gmm-cat-panel').forEach(panel => {
      const items     = $$('.gmm-cat-item', panel);
      const subpanels = $$('.gmm-subcat-panel', panel);
      if (!items.length) return;

      // Activate first item by default
      const activateItem = (item) => {
        items.forEach(i => i.classList.remove('is-active'));
        subpanels.forEach(p => p.classList.remove('is-active'));
        item.classList.add('is-active');
        const idx = item.getAttribute('data-cat-idx');
        const target = panel.querySelector(`.gmm-subcat-panel[data-cat-idx="${idx}"]`);
        if (target) target.classList.add('is-active');
      };

      activateItem(items[0]);

      items.forEach(item => {
        item.addEventListener('mouseenter', () => activateItem(item));

        item.addEventListener('keydown', (e) => {
          const i = items.indexOf(item);
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            items[Math.min(i + 1, items.length - 1)]?.querySelector('.gmm-cat-link')?.focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            items[Math.max(i - 1, 0)]?.querySelector('.gmm-cat-link')?.focus();
          }
        });
      });
    });
  }

  /* ── Bootstrap ────────────────────────────────────────────── */

  function initEMM() {
    if (window.innerWidth < 1024) return;

    $$('.has-emm-panel, .has-emm-dropdown').forEach(el => {
      // Remove any theme-added submenu class so theme dropdown CSS doesn't fire
      el.classList.remove('has-submenu');

      if (!el._emmController) {
        el._emmController = new EmmPanel(el);
      }
    });

    // Close on click outside (panels are in body so also check .emm-panel/.emm-dropdown)
    document.addEventListener('click', (e) => {
      const inTrigger = e.target.closest('.has-emm-panel, .has-emm-dropdown');
      const inPanel   = e.target.closest('.emm-panel, .emm-dropdown');
      if (!inTrigger && !inPanel) {
        $$('.has-emm-panel.focus, .has-emm-dropdown.focus').forEach(el => {
          el._emmController?._closeNow();
        });
      }
    });

    // Reposition on scroll (sticky header shifts nav bar bottom)
    window.addEventListener('scroll', () => {
      $$('.has-emm-panel.focus, .has-emm-dropdown.focus').forEach(el => {
        el._emmController?._repositionPanel();
      }, { passive: true });
    });

    // Reposition on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        $$('.has-emm-panel.focus, .has-emm-dropdown.focus').forEach(el => {
          el._emmController?._repositionPanel();
        });
      }, 120);
    });

    initCategoryHover();
  }

  function boot() {
    // Defer initEMM until after the sticky clone exists so portaling
    // captures all triggers (original + sticky) in one pass.
    // If the sticky clone is already present (section-header.js ran first),
    // or there is no sticky header at all, initialise immediately.
    if (document.querySelector('[data-sticky-header]') && !document.querySelector('.sticky-header')) {
      document.addEventListener('gilgal:sticky-header-ready', () => {
        initEMM();
        document.body.classList.add('emm-ready');
      }, { once: true });
    } else {
      initEMM();
      document.body.classList.add('emm-ready');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-render: clean up portaled panels, reset controllers, re-init
  document.addEventListener('shopify:section:load', () => {
    $$('.has-emm-panel.focus, .has-emm-dropdown.focus').forEach(el => {
      el._emmController?._closeNow();
    });
    $$('[data-emm-portaled]').forEach(p => p.remove());
    $$('.has-emm-panel, .has-emm-dropdown').forEach(el => {
      el._emmController = null;
    });
    boot();
  });

}());

/* GILGAL CUSTOM MEGA MENU END */
